import React from 'react';
import styled from 'styled-components';
import {Button, EMPHASIS, Text} from '@forecast-it/design-system';
import {removeSelectedLabel, selectionSummaryLabel} from './featureFlagRemoval';
import {FEATURE_FLAG_OVERLAY_BAR_BACKGROUND, FEATURE_FLAG_OVERLAY_BAR_TEXT} from './featureFlagSurface';

/**
 * Fixed to the viewport rather than the table, so the bar stays reachable while a long assignment list
 * is scrolled — the selection it summarises spans the whole list, not the part currently in view.
 */
const Bar = styled.div`
	position: fixed;
	left: 50%;
	transform: translateX(-50%);
	bottom: 24px;
	z-index: 70;
	display: flex;
	align-items: center;
	gap: 18px;
	padding: 14px 18px;
	border-radius: 8px;
	background: ${FEATURE_FLAG_OVERLAY_BAR_BACKGROUND};
	box-shadow: 0 8px 24px 0 rgba(35, 35, 52, 0.28);
`;

/* The typography component paints its own colour onto the element it renders, so the light-on-dark
   treatment has to be declared on the child rather than inherited from the bar. */
const Summary = styled.span`
	> * {
		color: ${FEATURE_FLAG_OVERLAY_BAR_TEXT};
	}
`;

const Actions = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
`;

/**
 * Cancel, drawn as the plain text the design asks for but built on a real button.
 *
 * A bare clickable span would be the closer match to the mock and would also be unreachable by tab and
 * unusable by Enter, which for the only way out of selection mode is not a trade worth making.
 */
const CancelButton = styled.button`
	all: unset;
	padding: 0 6px;
	cursor: pointer;
	color: ${FEATURE_FLAG_OVERLAY_BAR_TEXT};
	opacity: 0.72;

	&:hover:not(:disabled) {
		opacity: 1;
	}

	&:disabled {
		cursor: default;
		opacity: 0.4;
	}

	&:focus-visible {
		outline: 2px solid ${FEATURE_FLAG_OVERLAY_BAR_TEXT};
		outline-offset: 2px;
	}
`;

export interface FeatureFlagBulkRemoveBarProps {
	/** How many companies are ticked in the table below. */
	selectedCount: number;
	/** True while the removal this bar started is still in flight. */
	isRemoving: boolean;
	/** Leave selection mode, keeping every assignment. */
	onCancel: () => void;
	/** Remove the ticked companies. */
	onRemove: () => void;
}

/**
 * The strip that floats over the assignment table while companies are being picked for removal.
 *
 * Both controls are blocked while a removal is in flight: the mutation cannot be recalled, so a second
 * press could only fail against grants that no longer exist, and cancelling would drop the bar while
 * the table still shows the rows the response is about to clear.
 */
export function FeatureFlagBulkRemoveBar({
	selectedCount,
	isRemoving,
	onCancel,
	onRemove,
}: FeatureFlagBulkRemoveBarProps): JSX.Element {
	return (
		<Bar data-cy="feature-flag-bulk-remove-bar">
			<Summary>
				<Text>{selectionSummaryLabel(selectedCount)}</Text>
			</Summary>
			<Actions>
				<CancelButton data-cy="feature-flag-bulk-remove-cancel" disabled={isRemoving} onClick={onCancel} type="button">
					Cancel
				</CancelButton>
				<Button
					data-cy="feature-flag-bulk-remove-confirm"
					disabled={selectedCount === 0 || isRemoving}
					emphasis={EMPHASIS.HIGH}
					icon="trash"
					onPress={onRemove}
				>
					{removeSelectedLabel(selectedCount)}
				</Button>
			</Actions>
		</Bar>
	);
}
